/* =============================================================================
   Theme runtime

   Vanilla JS, no dependencies, no build step. Everything is a custom element
   so sections stay self-contained and survive theme-editor re-renders.

   Sections:
     1.  Config + utilities
     2.  Cart store (single source of truth, event driven)
     3.  Cart drawer / cart items / free shipping bar
     4.  Product form, variant picker, quantity, quick add
     5.  Sticky add to cart
     6.  Media gallery
     7.  Predictive search
     8.  Motion — scroll reveal
     9.  UI odds and ends — marquee, tabs, countdown, carousel, toast
     10. Header metrics, prefetch, theme editor hooks
   ========================================================================== */

(() => {
  'use strict';

  /* ==========================================================================
     1. Config + utilities
     ========================================================================== */

  // A malformed config must never take the whole runtime down with it — an
  // uncaught throw here would stop every custom element from registering.
  let config = {};
  try {
    const configEl = document.getElementById('MasterThemeConfig');
    if (configEl) config = JSON.parse(configEl.textContent) || {};
  } catch (error) {
    console.error('[MasterTheme] Could not parse theme config:', error);
  }

  const routes = config.routes || {};
  const strings = config.strings || {};

  const $ = (selector, scope = document) => scope.querySelector(selector);
  const $$ = (selector, scope = document) => Array.from(scope.querySelectorAll(selector));

  const on = (target, type, handler, options) => target.addEventListener(type, handler, options);

  const debounce = (fn, wait = 250) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  /**
   * Formats cents using the shop's money format string.
   * Supports the {{amount}}, {{amount_no_decimals}}, {{amount_with_comma_separator}}
   * and {{amount_no_decimals_with_comma_separator}} placeholders.
   */
  const formatMoney = (cents, format = config.moneyFormat || '${{amount}}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents) || 0;

    const withDelimiters = (num, precision = 2, thousands = ',', decimal = '.') => {
      const fixed = (num / 100.0).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const spaced = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      return fraction ? `${spaced}${decimal}${fraction}` : spaced;
    };

    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholder);
    if (!match) return format;

    let formatted;
    switch (match[1]) {
      case 'amount_no_decimals':
        formatted = withDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        formatted = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        formatted = withDelimiters(value, 2, ' ', ',');
        break;
      default:
        formatted = withDelimiters(value, 2);
    }

    return format.replace(placeholder, formatted);
  };

  /**
   * Fills a translated string that Liquid rendered with a `[]` sentinel.
   *
   * Anything this file rewrites on variant change must not invent its own
   * English. The section renders `'…' | t: amount: '[]'` into a data attribute,
   * and we substitute here, so a shopper on a translated storefront keeps
   * seeing their own language after they pick a variant.
   *
   * Falls back to the bare value if the attribute is missing, which is the
   * least wrong thing to show.
   */
  const fillTemplate = (template, value) =>
    template && template.includes('[]') ? template.replace('[]', value) : value;

  const lockScroll = (locked) => {
    document.documentElement.classList.toggle('scroll-locked', locked);
  };

  const trapFocus = (container) => {
    const focusable = $$(
      'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])',
      container
    ).filter((el) => el.offsetParent !== null);
    if (!focusable.length) return () => {};

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    const handler = (event) => {
      if (event.key !== 'Tab') return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    container.addEventListener('keydown', handler);
    first.focus({ preventScroll: true });
    return () => container.removeEventListener('keydown', handler);
  };

  window.MasterTheme = { config, formatMoney, $, $$ };

  /* ==========================================================================
     2. Cart store
     ========================================================================== */

  const CartStore = {
    state: null,
    _pending: null,

    /** Broadcasts cart state to every listener. */
    emit(detail = {}) {
      document.dispatchEvent(
        new CustomEvent('cart:updated', { detail: { cart: this.state, ...detail } })
      );
    },

    error(message) {
      document.dispatchEvent(
        new CustomEvent('cart:error', { detail: { message } })
      );
    },

    /** List of section ids that should be re-rendered on every cart mutation. */
    sectionsToRender() {
      return $$('[data-cart-section]')
        .map((el) => el.dataset.cartSection)
        .filter((id, index, list) => id && list.indexOf(id) === index);
    },

    applySections(sections) {
      if (!sections) return;
      let replaced = false;

      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const target = $(`[data-cart-section="${id}"]`);
        if (!target) return;
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = parsed.querySelector(`[data-cart-section="${id}"]`);
        if (!source) return;
        target.innerHTML = source.innerHTML;
        replaced = true;
      });

      if (!replaced) return;

      // Freshly injected nodes were never seen by the reveal observer, so
      // anything carrying [data-reveal] would sit at opacity 0 forever —
      // this is what used to make the cart-page upsell row vanish after any
      // quantity change.
      Motion.scan(document);

      // innerHTML replacement detaches whatever had focus and invalidates the
      // open drawer's focus trap, which closed over now-removed nodes.
      document.dispatchEvent(new CustomEvent('sections:rendered'));
    },

    async request(url, body) {
      const sections = this.sectionsToRender();
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          ...body,
          sections: sections.length ? sections : undefined,
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.description || data.message || strings.cartError);
      }
      return data;
    },

    async fetchCart() {
      const response = await fetch(`${routes.cart || '/cart'}.js`, {
        headers: { Accept: 'application/json' },
      });
      this.state = await response.json();
      return this.state;
    },

    async add(items, { openDrawer = true } = {}) {
      try {
        const payload = Array.isArray(items) ? items : [items];
        const data = await this.request(routes.cartAdd || '/cart/add.js', { items: payload });
        this.applySections(data.sections);
        await this.fetchCart();
        this.emit({ action: 'add', added: data.items || payload });

        if (openDrawer && config.cartType === 'drawer') {
          document.dispatchEvent(new CustomEvent('cart-drawer:open'));
        }
        return data;
      } catch (error) {
        this.error(error.message);
        throw error;
      }
    },

    /** `line` is 1-indexed as required by /cart/change.js */
    async changeLine(line, quantity) {
      try {
        const data = await this.request(routes.cartChange || '/cart/change.js', {
          line,
          quantity,
        });
        this.state = data;
        this.applySections(data.sections);
        this.emit({ action: 'change', line, quantity });
        return data;
      } catch (error) {
        this.error(error.message);
        throw error;
      }
    },

    async update(updates) {
      try {
        const data = await this.request(routes.cartUpdate || '/cart/update.js', { updates });
        this.state = data;
        this.applySections(data.sections);
        this.emit({ action: 'update' });
        return data;
      } catch (error) {
        this.error(error.message);
        throw error;
      }
    },
  };

  window.MasterTheme.cart = CartStore;

  /* ==========================================================================
     3. Cart drawer, cart items, free shipping bar
     ========================================================================== */

  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.overlay = $('.drawer__overlay', this);
      this.panel = $('.drawer__panel', this);
      this.releaseFocus = null;

      on(this, 'click', (event) => {
        if (event.target.closest('[data-drawer-close]') || event.target === this.overlay) {
          this.close();
        }
      });

      this._onKeydown = (event) => {
        if (event.key === 'Escape') this.close();
      };

      this._onOpen = () => this.open();
      document.addEventListener('cart-drawer:open', this._onOpen);

      // After a re-render the trapped nodes are detached and focus has fallen
      // back to <body>, outside a dialog still marked aria-modal. Re-arm.
      this._onRendered = () => {
        if (!this.classList.contains('is-open')) return;
        if (this.releaseFocus) this.releaseFocus();
        this.releaseFocus = trapFocus(this.panel);
      };
      document.addEventListener('sections:rendered', this._onRendered);
    }

    disconnectedCallback() {
      document.removeEventListener('cart-drawer:open', this._onOpen);
      document.removeEventListener('sections:rendered', this._onRendered);
      document.removeEventListener('keydown', this._onKeydown);
    }

    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      document.addEventListener('keydown', this._onKeydown);
      // Wait for the transition to start so focus does not fight the animation.
      requestAnimationFrame(() => {
        this.releaseFocus = trapFocus(this.panel);
      });
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      document.removeEventListener('keydown', this._onKeydown);
      if (this.releaseFocus) this.releaseFocus();
      const opener = $('[data-cart-toggle]');
      if (opener) opener.focus({ preventScroll: true });
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  /** A single cart line. Owns its own quantity controls and remove button. */
  class CartItem extends HTMLElement {
    connectedCallback() {
      this.line = Number(this.dataset.line);

      on(this, 'click', (event) => {
        const remove = event.target.closest('[data-cart-remove]');
        if (remove) {
          event.preventDefault();
          this.setQuantity(0);
        }
      });

      on(this, 'quantity:change', (event) => {
        this.setQuantity(event.detail.value);
      });
    }

    async setQuantity(quantity) {
      this.classList.add('is-loading');
      this.setAttribute('aria-busy', 'true');
      try {
        await CartStore.changeLine(this.line, quantity);
      } finally {
        this.classList.remove('is-loading');
        this.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('cart-item', CartItem);

  /**
   * Free shipping progress bar. Recalculates on every cart update so it works
   * in the drawer, on the cart page and in the announcement bar simultaneously.
   */
  class ShippingBar extends HTMLElement {
    connectedCallback() {
      this.threshold = Number(this.dataset.threshold) || (config.shipping || {}).threshold || 0;
      this.messageEl = $('[data-shipping-message]', this);
      this.fillEl = $('.progress__fill', this);

      this._onUpdate = (event) => this.render(event.detail.cart);
      document.addEventListener('cart:updated', this._onUpdate);
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this._onUpdate);
    }

    render(cart) {
      if (!cart || !this.threshold) return;
      const total = cart.total_price || 0;
      const remaining = Math.max(this.threshold - total, 0);
      const percent = Math.min((total / this.threshold) * 100, 100);

      if (this.fillEl) this.fillEl.style.setProperty('--progress', `${percent}%`);
      this.classList.toggle('progress--complete', remaining === 0);

      if (this.messageEl) {
        const shipping = config.shipping || {};
        this.messageEl.textContent =
          remaining === 0
            ? shipping.successText || ''
            : (shipping.progressText || '').replace('[amount]', formatMoney(remaining));
      }
    }
  }
  customElements.define('shipping-bar', ShippingBar);

  /* ==========================================================================
     4. Product form, variant picker, quantity, quick add
     ========================================================================== */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this);
      if (!this.input) return;

      on(this, 'click', (event) => {
        const button = event.target.closest('[data-quantity-step]');
        if (!button) return;
        event.preventDefault();
        const step = Number(button.dataset.quantityStep);
        this.setValue(Number(this.input.value) + step);
      });

      on(this.input, 'change', () => this.setValue(Number(this.input.value)));
    }

    setValue(value) {
      // `Number('0') || 1` evaluates to 1 because 0 is falsy, which silently
      // broke min="0" and stopped shoppers stepping a cart line down to zero.
      const rawMin = this.input.getAttribute('min');
      const min = rawMin === null || rawMin === '' ? 1 : Number(rawMin);
      const max = this.input.max ? Number(this.input.max) : Infinity;
      const next = Math.min(Math.max(value, min), max);

      if (next === Number(this.input.value) && this.dataset.silent) return;
      this.input.value = next;
      this.dispatchEvent(
        new CustomEvent('quantity:change', { bubbles: true, detail: { value: next } })
      );
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /**
   * Variant picker.
   *
   * Reads the product's variant data from an inline JSON script rather than
   * hitting the network, then updates price, availability, media, SKU and the
   * URL. Unavailable option values are marked so shoppers can see what is out
   * of stock before they click.
   */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.productUrl = this.dataset.url;
      this.updateUrl = this.dataset.updateUrl !== 'false';

      const dataEl = $('[data-variant-data]', this);
      this.variants = dataEl ? JSON.parse(dataEl.textContent) : [];

      on(this, 'change', () => this.onChange());
      this.updateAvailability();
    }

    get selectedOptions() {
      return $$('fieldset', this).map((fieldset) => {
        const checked = $('input:checked', fieldset);
        if (checked) return checked.value;
        const select = $('select', fieldset);
        return select ? select.value : null;
      });
    }

    findVariant(options = this.selectedOptions) {
      return this.variants.find((variant) =>
        variant.options.every((option, index) => option === options[index])
      );
    }

    onChange() {
      this.updateAvailability();

      // If the combination the shopper just made does not exist, the option
      // they had selected is now hidden — leaving the buy button stuck on
      // "Unavailable" with no visible way back. Move them to the nearest
      // option that does exist instead of stranding them.
      if (this.reselectStrandedOptions()) {
        this.updateAvailability();
      }

      const variant = this.findVariant();
      this.updateSelectedLabels();

      if (!variant) {
        this.dispatchEvent(
          new CustomEvent('variant:change', { bubbles: true, detail: { variant: null } })
        );
        return;
      }

      if (this.updateUrl && this.productUrl) {
        window.history.replaceState({}, '', `${this.productUrl}?variant=${variant.id}`);
      }

      const input = $(`#VariantInput-${this.sectionId}`);
      if (input) {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.dispatchEvent(
        new CustomEvent('variant:change', { bubbles: true, detail: { variant } })
      );
    }

    /**
     * Marks each option value as unavailable when no variant exists that
     * combines it with the currently selected values to its left.
     */
    updateAvailability() {
      const selected = this.selectedOptions;

      $$('fieldset', this).forEach((fieldset, optionIndex) => {
        $$('input, option', fieldset).forEach((node) => {
          const candidate = [...selected];
          candidate[optionIndex] = node.value;

          const exists = this.variants.some((variant) =>
            variant.options.every(
              (option, index) => index > optionIndex || option === candidate[index]
            ) && variant.options[optionIndex] === node.value
          );

          const available = this.variants.some(
            (variant) =>
              variant.available &&
              variant.options.every(
                (option, index) => index === optionIndex || option === selected[index]
              ) &&
              variant.options[optionIndex] === node.value
          );

          const wrapper = node.closest('label') || node;
          if (node.tagName === 'OPTION') {
            node.disabled = !exists;
          }
          wrapper.classList.toggle('is-unavailable', exists && !available);
          wrapper.classList.toggle('is-nonexistent', !exists);
        });
      });
    }

    /**
     * Returns true if it had to move any selection.
     * Prefers an available option, falling back to one that merely exists.
     */
    reselectStrandedOptions() {
      let moved = false;

      $$('fieldset', this).forEach((fieldset) => {
        const checked = $('input:checked', fieldset);
        if (!checked) return;
        const label = checked.closest('label');
        if (!label || !label.classList.contains('is-nonexistent')) return;

        const candidates = $$('input', fieldset).filter((input) => {
          const wrapper = input.closest('label');
          return wrapper && !wrapper.classList.contains('is-nonexistent');
        });

        const preferred =
          candidates.find((input) => {
            const wrapper = input.closest('label');
            return wrapper && !wrapper.classList.contains('is-unavailable');
          }) || candidates[0];

        if (preferred) {
          preferred.checked = true;
          moved = true;
        }
      });

      return moved;
    }

    updateSelectedLabels() {
      $$('[data-option-selected]', this).forEach((el) => {
        const fieldset = el.closest('fieldset') || el.parentElement.querySelector('fieldset');
        const checked = fieldset ? $('input:checked', fieldset) : null;
        if (checked) el.textContent = checked.value;
      });
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /**
   * Wraps a product form. Intercepts submit, adds via AJAX, and keeps the
   * button in a sane state throughout. Falls back to a normal form post if
   * JavaScript fails.
   */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      if (!this.form) return;

      this.button = $('[type="submit"]', this.form);
      this.errorEl = $('[data-form-error]', this);

      on(this.form, 'submit', (event) => this.onSubmit(event));

      this._onVariantChange = (event) => this.onVariantChange(event.detail.variant);
      const scope = this.closest('[data-product-root]') || document;
      scope.addEventListener('variant:change', this._onVariantChange);
      this._scope = scope;
    }

    disconnectedCallback() {
      if (this._scope) this._scope.removeEventListener('variant:change', this._onVariantChange);
    }

    onVariantChange(variant) {
      if (!this.button) return;
      const label = $('.button__text', this.button) || this.button;

      if (!variant) {
        this.button.disabled = true;
        label.textContent = strings.unavailable || 'Unavailable';
      } else if (!variant.available) {
        this.button.disabled = true;
        label.textContent = strings.soldOut || 'Sold out';
      } else {
        this.button.disabled = false;
        label.textContent = this.button.dataset.defaultLabel || strings.addToCart || 'Add to cart';
      }
    }

    async onSubmit(event) {
      // Let the browser handle it if the cart is a full page reload experience.
      if (this.dataset.ajax === 'false') return;
      event.preventDefault();
      if (this.button && this.button.getAttribute('aria-busy') === 'true') return;

      const formData = new FormData(this.form);
      const item = {
        id: Number(formData.get('id')),
        quantity: Number(formData.get('quantity')) || 1,
      };

      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) item.selling_plan = Number(sellingPlan);

      const properties = {};
      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && value !== '') properties[match[1]] = value;
      });
      if (Object.keys(properties).length) item.properties = properties;

      this.setBusy(true);
      if (this.errorEl) this.errorEl.hidden = true;

      try {
        await CartStore.add(item);
      } catch (error) {
        if (this.errorEl) {
          this.errorEl.textContent = error.message;
          this.errorEl.hidden = false;
        }
      } finally {
        this.setBusy(false);
      }
    }

    setBusy(busy) {
      if (!this.button) return;
      this.button.setAttribute('aria-busy', String(busy));
      this.button.disabled = busy;
    }
  }
  customElements.define('product-form', ProductForm);

  /** Single-click add for product cards. Falls back to the PDP when a product has options. */
  class QuickAdd extends HTMLElement {
    connectedCallback() {
      this.button = $('button', this);
      if (!this.button) return;

      on(this.button, 'click', async (event) => {
        event.preventDefault();
        const variantId = Number(this.dataset.variantId);
        if (!variantId) return;

        this.button.setAttribute('aria-busy', 'true');
        try {
          await CartStore.add({ id: variantId, quantity: 1 });
        } catch (error) {
          /* Toast already surfaced the message. */
        } finally {
          this.button.setAttribute('aria-busy', 'false');
        }
      });
    }
  }
  customElements.define('quick-add', QuickAdd);

  /**
   * Volume / quantity-break tiers.
   *
   * Selecting a tier drives the real quantity input, so what gets added to the
   * cart is always what the shopper picked. The discounted figures shown are a
   * projection of a Shopify automatic discount — the theme never writes a
   * price into the cart, because Shopify would ignore it anyway and the
   * shopper would see a different number at checkout.
   */
  class VolumeTiers extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('[data-product-root]') || document;
      this.basePrice = Number(this.dataset.basePrice) || 0;
      this.format = this.dataset.savingsFormat || 'percent';
      this.eachTemplate = this.dataset.eachTemplate || '';
      this.saveTemplate = this.dataset.saveTemplate || '';

      on(this, 'change', (event) => {
        if (event.target.matches('input[type="radio"]')) this.applyQuantity();
      });

      this._onVariantChange = (event) => {
        const variant = event.detail.variant;
        if (!variant) return;
        this.basePrice = variant.price;
        this.render();
      };
      this.root.addEventListener('variant:change', this._onVariantChange);

      // Keep the tier highlight honest if the shopper edits quantity directly,
      // rather than showing "3 bottles / Best value" while adding one unit.
      this._onQuantityChange = (event) => {
        const input = event.target;
        if (!input.matches || !input.matches('input[name="quantity"]')) return;
        if (this._writing) return;
        this.syncFromQuantity(Number(input.value));
      };
      this.root.addEventListener('change', this._onQuantityChange);

      this.applyQuantity();
    }

    disconnectedCallback() {
      this.root.removeEventListener('variant:change', this._onVariantChange);
      this.root.removeEventListener('change', this._onQuantityChange);
    }

    get checked() {
      return $('input[type="radio"]:checked', this);
    }

    /**
     * Finds the quantity field this widget drives.
     *
     * The Quantity block is optional and merchants routinely remove it for a
     * cleaner PDP. When that happened this widget used to silently do nothing,
     * so every shopper choosing a 3-pack added a single unit at full price.
     * If no field exists, own one.
     */
    get quantityInput() {
      const existing = $('[data-product-quantity] input', this.root);
      if (existing) return existing;

      const form = $(`#ProductForm-${this.dataset.section}`);
      if (!form) return null;

      if (!this._ownInput) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'quantity';
        input.value = '1';
        form.appendChild(input);
        this._ownInput = input;
      }
      return this._ownInput;
    }

    applyQuantity() {
      const selected = this.checked;
      if (!selected) return;

      const input = this.quantityInput;
      if (!input) return;

      this._writing = true;
      input.value = selected.value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this._writing = false;
    }

    syncFromQuantity(quantity) {
      const match = $$('input[type="radio"]', this).find(
        (radio) => Number(radio.value) === quantity
      );
      if (match) {
        match.checked = true;
      } else {
        const current = this.checked;
        if (current) current.checked = false;
      }
    }

    render() {
      $$('label', this).forEach((label) => {
        const input = $('input[type="radio"]', label);
        if (!input) return;

        const qty = Number(input.value) || 1;
        const discount = Number(input.dataset.discount) || 0;

        // All money maths in integer cents. No floats, ever.
        const lineTotal = this.basePrice * qty;
        const saved = Math.round((lineTotal * discount) / 100);
        const total = lineTotal - saved;
        const perUnit = Math.round(total / qty);

        const totalEl = $('[data-tier-total]', label);
        const compareEl = $('[data-tier-compare]', label);
        const unitEl = $('[data-tier-unit]', label);
        const savingsEl = $('[data-tier-savings]', label);

        if (totalEl) totalEl.textContent = formatMoney(total);
        if (compareEl) compareEl.textContent = formatMoney(lineTotal);
        if (unitEl) unitEl.textContent = fillTemplate(this.eachTemplate, formatMoney(perUnit));
        if (savingsEl) {
          const amount = this.format === 'amount' ? formatMoney(saved) : `${discount}%`;
          savingsEl.textContent = fillTemplate(this.saveTemplate, amount);
        }
      });
    }
  }
  customElements.define('volume-tiers', VolumeTiers);

  /**
   * Subscribe & save.
   *
   * The frequency dropdown rewrites the value of the subscribe radio so the
   * form posts the selling plan the shopper actually chose.
   */
  class SubscriptionPicker extends HTMLElement {
    connectedCallback() {
      this.subscribeInput = $('[data-subscribe-input]', this);
      this.frequency = $('[data-frequency-select]', this);
      if (!this.subscribeInput) return;

      // Prices per variant, so the cards follow the selected variant.
      const dataEl = $('[data-subscription-data]', this);
      try {
        this.priceMap = dataEl ? JSON.parse(dataEl.textContent) : null;
      } catch (error) {
        this.priceMap = null;
      }
      if (this.priceMap) {
        this._scope = this.closest('[data-product-root]') || document;
        this._onVariantChange = (event) => this.onVariantChange(event.detail.variant);
        this._scope.addEventListener('variant:change', this._onVariantChange);
      }

      if (this.frequency) {
        // Keep the dropdown and the radio in sync from the start.
        this.frequency.value = this.subscribeInput.value;

        on(this.frequency, 'change', () => {
          this.subscribeInput.value = this.frequency.value;
          if (!this.subscribeInput.checked) {
            this.subscribeInput.checked = true;
          }
          this.subscribeInput.dispatchEvent(new Event('change', { bubbles: true }));
        });

        // Clicking the dropdown should select the subscribe option, not just
        // change a value on an unselected card.
        on(this.frequency, 'click', (event) => event.stopPropagation());
      }
    }

    disconnectedCallback() {
      if (this._scope && this._onVariantChange) {
        this._scope.removeEventListener('variant:change', this._onVariantChange);
      }
    }

    /**
     * Re-prices both cards for the newly selected variant. If the plan the
     * shopper had chosen is not offered on that variant, they fall back to the
     * one-time purchase rather than posting a plan Shopify would reject.
     */
    onVariantChange(variant) {
      const entry = variant && this.priceMap[String(variant.id)];
      if (!entry) return;

      const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
      const plans = entry.plans || {};
      const planIds = Object.keys(plans);
      const card = $('[data-subscribe-card]', this);
      const oneTime = $('[data-onetime-input]', this);
      const wasSubscribed = this.subscribeInput.checked;

      // Frequency options: only plans offered on this variant, at this variant's price.
      let firstOffered = null;
      if (this.frequency) {
        Array.from(this.frequency.options).forEach((option) => {
          const offered = has(plans, option.value);
          option.hidden = !offered;
          option.disabled = !offered;
          if (!offered) return;
          if (!firstOffered) firstOffered = option.value;
          const price = plans[option.value];
          option.textContent =
            (option.dataset.planName || option.textContent) +
            (price !== entry.price ? ' | ' + formatMoney(price) : '');
        });
      }

      if (card) card.hidden = planIds.length === 0;

      let planId = this.subscribeInput.value;
      if (!has(plans, planId)) {
        if ((wasSubscribed || planIds.length === 0) && oneTime && !oneTime.checked) {
          oneTime.checked = true;
          oneTime.dispatchEvent(new Event('change', { bubbles: true }));
        }
        planId = firstOffered || planIds[0] || planId;
        this.subscribeInput.value = planId;
        if (this.frequency && firstOffered) this.frequency.value = firstOffered;
      }

      const subPrice = has(plans, planId) ? plans[planId] : entry.price;
      const savings = entry.price - subPrice;
      const percent = entry.price > 0 && savings > 0 ? Math.floor((savings * 100) / entry.price) : 0;

      const oneTimeEl = $('[data-onetime-price]', this);
      if (oneTimeEl) oneTimeEl.textContent = formatMoney(entry.price);
      const subEl = $('[data-sub-price]', this);
      if (subEl) subEl.textContent = formatMoney(subPrice);

      const compare = $('[data-sub-compare]', this);
      if (compare) {
        compare.textContent = formatMoney(entry.price);
        compare.hidden = !(savings > 0);
      }
      const badge = $('[data-sub-badge]', this);
      if (badge) {
        badge.textContent = (this.dataset.saveTemplate || '').replace('__PERCENT__', percent);
        badge.hidden = percent <= 0;
      }
    }
  }
  customElements.define('subscription-picker', SubscriptionPicker);

  /* ==========================================================================
     5. Sticky add to cart
     ========================================================================== */

  /**
   * Reveals a compact buy bar once the real add-to-cart button scrolls out of
   * view, and hides it again when the shopper scrolls back up to it.
   */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const targetSelector = this.dataset.watch;
      const target = targetSelector ? $(targetSelector) : null;
      if (!target) return;

      this.button = $('[data-sticky-submit]', this);

      // The sticky bar sits outside [data-product-root], so it has to listen on
      // the document. Without this it kept saying "Add to cart" for a sold-out
      // variant and requestSubmit() bypassed the disabled real button.
      this._onVariantChange = (event) => {
        if (!this.button) return;
        const variant = event.detail.variant;
        const label = $('[data-sticky-label]', this) || this.button;

        if (!variant) {
          this.button.disabled = true;
          label.textContent = strings.unavailable || 'Unavailable';
        } else if (!variant.available) {
          this.button.disabled = true;
          label.textContent = strings.soldOut || 'Sold out';
        } else {
          this.button.disabled = false;
          label.textContent = strings.addToCart || 'Add to cart';
        }
      };
      document.addEventListener('variant:change', this._onVariantChange);

      this.observer = new IntersectionObserver(
        ([entry]) => {
          const past = entry.boundingClientRect.top < 0 && !entry.isIntersecting;
          this.classList.toggle('is-visible', past);
        },
        { rootMargin: '0px 0px -100% 0px', threshold: 0 }
      );
      this.observer.observe(target);

      on(this, 'click', (event) => {
        const trigger = event.target.closest('[data-sticky-submit]');
        if (!trigger || trigger.disabled) return;
        event.preventDefault();
        const form = $(this.dataset.form);
        if (!form) return;
        // Respect the real add-to-cart button's disabled state.
        const realButton = $('[type="submit"]', form);
        if (realButton && realButton.disabled) return;
        form.requestSubmit();
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      document.removeEventListener('variant:change', this._onVariantChange);
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ==========================================================================
     6. Media gallery
     ========================================================================== */

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = $('[data-gallery-viewport]', this);
      this.thumbs = $$('[data-gallery-thumb]', this);
      this.slides = $$('[data-gallery-slide]', this);

      this.thumbs.forEach((thumb) => {
        on(thumb, 'click', (event) => {
          event.preventDefault();
          this.goTo(thumb.dataset.galleryThumb);
        });
      });

      if (this.viewport) {
        on(
          this.viewport,
          'scroll',
          debounce(() => this.syncFromScroll(), 120)
        );
      }

      // Jump to the variant's featured image when the variant changes.
      const scope = this.closest('[data-product-root]') || document;
      this._onVariantChange = (event) => {
        const variant = event.detail.variant;
        if (variant && variant.featured_media) this.goTo(String(variant.featured_media.id));
      };
      scope.addEventListener('variant:change', this._onVariantChange);
      this._scope = scope;
    }

    disconnectedCallback() {
      if (this._scope) this._scope.removeEventListener('variant:change', this._onVariantChange);
    }

    goTo(mediaId) {
      const slide = this.slides.find((el) => el.dataset.mediaId === String(mediaId));
      if (!slide) return;

      if (this.viewport && this.viewport.scrollWidth > this.viewport.clientWidth) {
        this.viewport.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      } else {
        slide.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
      this.setActive(mediaId);
    }

    syncFromScroll() {
      if (!this.viewport) return;
      const center = this.viewport.scrollLeft + this.viewport.clientWidth / 2;
      const current = this.slides.find(
        (slide) => slide.offsetLeft <= center && slide.offsetLeft + slide.offsetWidth > center
      );
      if (current) this.setActive(current.dataset.mediaId);
    }

    setActive(mediaId) {
      this.thumbs.forEach((thumb) => {
        const active = thumb.dataset.galleryThumb === String(mediaId);
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ==========================================================================
     7. Predictive search
     ========================================================================== */

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = $('input[type="search"]', this);
      this.results = $('[data-search-results]', this);
      if (!this.input || !this.results) return;

      this.controller = null;

      on(
        this.input,
        'input',
        debounce(() => this.search(), 260)
      );

      on(document, 'click', (event) => {
        if (!this.contains(event.target)) this.close();
      });

      on(this.input, 'keydown', (event) => {
        if (event.key === 'Escape') this.close();
      });
    }

    async search() {
      const query = this.input.value.trim();
      if (query.length < 2) return this.close();

      if (this.controller) this.controller.abort();
      this.controller = new AbortController();

      const resources = [];
      if (config.search === undefined || true) {
        resources.push('product');
      }

      const params = new URLSearchParams({
        q: query,
        'resources[type]': this.dataset.resources || 'product,collection,page,article',
        'resources[limit]': this.dataset.limit || '6',
        'section_id': 'predictive-search',
      });

      try {
        const response = await fetch(`${routes.predictiveSearch || '/search/suggest'}?${params}`, {
          signal: this.controller.signal,
        });
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const source = parsed.querySelector('[data-search-results]');
        this.results.innerHTML = source ? source.innerHTML : '';
        this.open();
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      }
    }

    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-expanded', 'false');
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ==========================================================================
     8. Motion — scroll reveal
     ========================================================================== */

  const Motion = {
    observer: null,

    init() {
      const enabled =
        document.documentElement.dataset.motionEnabled === 'true' ||
        (config.motion || {}).enabled;

      // Only now, with JS confirmed running, is it safe to let base.css hide
      // reveal targets at opacity 0 — otherwise a JS failure hides the page.
      if (!enabled) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (!('IntersectionObserver' in window)) return;

      document.documentElement.classList.add('motion-on');

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            this.observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
      );

      this.scan();

      // Backstop: anything the observer never fired for (inside a collapsed
      // parent, an off-screen tab, a stalled paint) gets revealed anyway.
      // Content being visible always beats content being animated.
      setTimeout(() => this.revealAll(), 3000);
    },

    revealAll() {
      $$('[data-reveal]:not(.is-revealed)').forEach((el) => {
        const box = el.getBoundingClientRect();
        // Only force-reveal what is at or above the fold; the rest keeps its
        // scroll animation.
        if (box.top < window.innerHeight * 1.5) el.classList.add('is-revealed');
      });
    },

    scan(scope = document) {
      if (!this.observer) return;
      const style = (config.motion || {}).style || 'rise';
      const stagger = (config.motion || {}).stagger || 0;

      $$('[data-reveal]', scope).forEach((el) => {
        if (el.dataset.revealBound) return;
        el.dataset.revealBound = 'true';
        if (el.getAttribute('data-reveal') === '') el.setAttribute('data-reveal', style);
        this.observer.observe(el);
      });

      // Stagger direct children of any [data-reveal-group].
      $$('[data-reveal-group]', scope).forEach((group) => {
        Array.from(group.children).forEach((child, index) => {
          if (!child.hasAttribute('data-reveal')) {
            child.setAttribute('data-reveal', style);
            child.dataset.revealBound = 'true';
            this.observer.observe(child);
          }
          child.style.setProperty('--reveal-delay', `${index * stagger}ms`);
        });
      });
    },
  };

  /* ==========================================================================
     9. UI odds and ends
     ========================================================================== */

  /** Duplicates its content until the track is wide enough to loop seamlessly. */
  class MarqueeRow extends HTMLElement {
    connectedCallback() {
      const track = $('.marquee__track', this);
      if (!track) return;

      const fill = () => {
        const parentWidth = this.offsetWidth;
        let guard = 0;
        while (track.scrollWidth < parentWidth * 2 && guard < 10) {
          track.append(...Array.from(track.children).map((child) => child.cloneNode(true)));
          guard += 1;
        }
        // A second identical track makes the -100% keyframe loop invisibly.
        if (!this.dataset.cloned) {
          this.append(track.cloneNode(true));
          this.dataset.cloned = 'true';
        }
      };

      if (this.offsetWidth) fill();
      else requestAnimationFrame(fill);
    }
  }
  customElements.define('marquee-row', MarqueeRow);

  class TabGroup extends HTMLElement {
    connectedCallback() {
      this.tabs = $$('[role="tab"]', this);
      this.panels = $$('[role="tabpanel"]', this);

      this.tabs.forEach((tab) => {
        on(tab, 'click', () => this.select(tab.getAttribute('aria-controls')));
        on(tab, 'keydown', (event) => {
          const index = this.tabs.indexOf(tab);
          let next = null;
          if (event.key === 'ArrowRight') next = this.tabs[(index + 1) % this.tabs.length];
          if (event.key === 'ArrowLeft') next = this.tabs[(index - 1 + this.tabs.length) % this.tabs.length];
          if (next) {
            event.preventDefault();
            next.focus();
            this.select(next.getAttribute('aria-controls'));
          }
        });
      });
    }

    select(panelId) {
      this.tabs.forEach((tab) => {
        const active = tab.getAttribute('aria-controls') === panelId;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.panels.forEach((panel) => {
        panel.hidden = panel.id !== panelId;
      });
    }
  }
  customElements.define('tab-group', TabGroup);

  /**
   * Countdown to a fixed date, or a rolling daily deadline (for "order within
   * X for delivery tomorrow" style urgency). Hides itself when it expires
   * unless it is set to roll over.
   */
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      this.mode = this.dataset.mode || 'date';
      this.output = $('[data-countdown-output]', this);
      this.target = this.resolveTarget();
      if (!this.target) return;

      this.tick();
      this.interval = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.interval);
    }

    resolveTarget() {
      if (this.mode === 'daily') {
        const [hours, minutes] = (this.dataset.deadline || '17:00').split(':').map(Number);
        const target = new Date();
        target.setHours(hours, minutes || 0, 0, 0);
        if (target <= new Date()) target.setDate(target.getDate() + 1);
        return target;
      }
      const parsed = new Date(this.dataset.date);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }

    tick() {
      const diff = this.target - new Date();

      if (diff <= 0) {
        if (this.mode === 'daily') {
          this.target = this.resolveTarget();
          return;
        }
        clearInterval(this.interval);
        if (this.dataset.hideOnEnd !== 'false') this.hidden = true;
        return;
      }

      const days = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      const pad = (n) => String(n).padStart(2, '0');

      $$('[data-unit]', this).forEach((el) => {
        const map = { days, hours, minutes, seconds };
        el.textContent = pad(map[el.dataset.unit]);
      });

      if (this.output) {
        this.output.textContent = days
          ? `${days}d ${pad(hours)}h ${pad(minutes)}m`
          : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
      }
    }
  }
  customElements.define('countdown-timer', CountdownTimer);

  /** Adds prev/next controls and dot state to a .scroller. */
  class ScrollCarousel extends HTMLElement {
    connectedCallback() {
      this.viewport = $('.scroller', this);
      if (!this.viewport) return;

      this.prev = $('[data-carousel-prev]', this);
      this.next = $('[data-carousel-next]', this);

      if (this.prev) on(this.prev, 'click', () => this.scrollByPage(-1));
      if (this.next) on(this.next, 'click', () => this.scrollByPage(1));

      on(
        this.viewport,
        'scroll',
        debounce(() => this.updateControls(), 100)
      );
      this.updateControls();
    }

    scrollByPage(direction) {
      const first = this.viewport.firstElementChild;
      const step = first ? first.offsetWidth + 16 : this.viewport.clientWidth * 0.8;
      this.viewport.scrollBy({ left: step * direction, behavior: 'smooth' });
    }

    updateControls() {
      const { scrollLeft, scrollWidth, clientWidth } = this.viewport;
      const atStart = scrollLeft <= 2;
      const atEnd = scrollLeft + clientWidth >= scrollWidth - 2;
      if (this.prev) this.prev.disabled = atStart;
      if (this.next) this.next.disabled = atEnd;
    }
  }
  customElements.define('scroll-carousel', ScrollCarousel);

  /**
   * Open/close for every non-cart drawer (mobile menu, search).
   *
   * The close behaviour is delegated at the document level rather than bound
   * per drawer, because the drawers are plain markup in the header — without
   * this, the close button, the overlay and Escape were all inert and a
   * shopper who opened the mobile menu was trapped behind a full-viewport
   * overlay with the page scroll locked.
   */
  const Drawers = {
    init() {
      on(document, 'click', (event) => {
        const closer = event.target.closest('[data-drawer-close]');
        const isOverlay =
          event.target.classList && event.target.classList.contains('drawer__overlay');
        if (!closer && !isOverlay) return;

        const drawer = event.target.closest('.drawer');
        // <cart-drawer> manages its own lifecycle.
        if (!drawer || drawer.tagName === 'CART-DRAWER') return;
        this.close(drawer);
      });

      on(document, 'keydown', (event) => {
        if (event.key !== 'Escape') return;
        const open = $$('.drawer.is-open').filter((d) => d.tagName !== 'CART-DRAWER').pop();
        if (open) this.close(open);
      });
    },

    open(drawer, toggle) {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      if (toggle) this.setExpanded(toggle, true);

      requestAnimationFrame(() => {
        const panel = $('.drawer__panel', drawer);
        if (panel) drawer._releaseFocus = trapFocus(panel);
      });
    },

    close(drawer) {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (drawer._releaseFocus) {
        drawer._releaseFocus();
        drawer._releaseFocus = null;
      }

      // Only release the scroll lock once nothing else is holding it.
      if (!$('.drawer.is-open')) lockScroll(false);

      const toggle = $(`drawer-toggle[data-target="#${drawer.id}"]`);
      if (toggle) {
        this.setExpanded(toggle, false);
        const button = $('button', toggle);
        if (button) button.focus({ preventScroll: true });
      }
    },

    /** aria-expanded belongs on the button, not on the wrapper element. */
    setExpanded(toggle, expanded) {
      const button = $('button', toggle) || toggle;
      button.setAttribute('aria-expanded', String(expanded));
    },
  };

  class DrawerToggle extends HTMLElement {
    connectedCallback() {
      on(this, 'click', () => {
        const target = $(this.dataset.target);
        if (!target) return;
        if (target.classList.contains('is-open')) {
          Drawers.close(target);
        } else {
          Drawers.open(target, this);
        }
      });
    }
  }
  customElements.define('drawer-toggle', DrawerToggle);

  const Toast = {
    init() {
      this.root = $('[data-toast]');
      if (!this.root) return;

      document.addEventListener('cart:error', (event) => this.show(event.detail.message, 'error'));
      document.addEventListener('cart:updated', (event) => {
        if (event.detail.action === 'add' && config.cartType !== 'drawer') {
          this.show(strings.addedToCart || 'Added to cart', 'success');
        }
      });
    },

    show(message, variant = 'info') {
      if (!this.root || !message) return;
      this.root.textContent = message;
      this.root.dataset.variant = variant;
      this.root.classList.add('is-visible');
      clearTimeout(this._timer);
      this._timer = setTimeout(() => this.root.classList.remove('is-visible'), 4000);
    },
  };

  /* ==========================================================================
     10. Header metrics, prefetch, theme editor hooks
     ========================================================================== */

  const Layout = {
    init() {
      this.measure();
      const observer = new ResizeObserver(() => this.measure());
      const header = $('[data-header]');
      const announcement = $('[data-announcement]');
      if (header) observer.observe(header);
      if (announcement) observer.observe(announcement);
      on(window, 'resize', debounce(() => this.measure(), 150));
    },

    measure() {
      const header = $('[data-header]');
      const announcement = $('[data-announcement]');
      const root = document.documentElement;
      root.style.setProperty('--header-height', `${header ? header.offsetHeight : 0}px`);
      root.style.setProperty(
        '--announcement-height',
        `${announcement ? announcement.offsetHeight : 0}px`
      );
    },
  };

  const Prefetch = {
    init() {
      if (document.body.dataset.prefetch !== 'true') return;
      if (navigator.connection && navigator.connection.saveData) return;

      const seen = new Set();
      const prefetch = (url) => {
        if (seen.has(url)) return;
        seen.add(url);
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = url;
        document.head.appendChild(link);
      };

      on(
        document,
        'pointerover',
        (event) => {
          const anchor = event.target.closest('a[href]');
          if (!anchor) return;
          const url = new URL(anchor.href, window.location.origin);
          if (url.origin !== window.location.origin) return;
          if (url.pathname.startsWith('/cart') || url.pathname.startsWith('/account')) return;
          prefetch(url.href);
        },
        { passive: true }
      );
    },
  };

  /**
   * Keeps the displayed price in step with the selected variant.
   *
   * Without this the PDP showed the product's "from" price permanently — a
   * shopper could select a $49 option, still read $29, and only discover the
   * real price in the cart.
   */
  const PriceSync = {
    init() {
      on(document, 'variant:change', (event) => {
        const variant = event.detail.variant;
        const root = event.target.closest('[data-product-root]') || document;
        $$('[data-price-root]', root).forEach((el) => this.render(el, variant));
      });
    },

    render(el, variant) {
      const current = $('[data-price-current]', el);
      const compare = $('[data-price-compare]', el);
      const savings = $('[data-price-savings]', el);
      const from = $('[data-price-from]', el);

      if (!variant) {
        el.setAttribute('aria-busy', 'true');
        return;
      }
      el.removeAttribute('aria-busy');

      // Once a variant is chosen there is nothing to be "from" any more.
      if (from) from.hidden = true;

      if (current) current.textContent = formatMoney(variant.price);

      const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;
      el.classList.toggle('price--on-sale', Boolean(hasCompare));

      if (compare) {
        compare.hidden = !hasCompare;
        if (hasCompare) compare.textContent = formatMoney(variant.compare_at_price);
      }

      if (savings) {
        savings.hidden = !hasCompare;
        if (hasCompare) {
          const saved = variant.compare_at_price - variant.price;
          const percent = Math.round((saved * 100) / variant.compare_at_price);
          const isAmount = savings.dataset.format === 'amount';
          savings.textContent = fillTemplate(
            isAmount ? savings.dataset.templateAmount : savings.dataset.templatePercent,
            isAmount ? formatMoney(saved) : `${percent}%`
          );
        }
      }
    },
  };

  /** Header cart badge. Nothing else was ever updating it. */
  const CartBadge = {
    init() {
      document.addEventListener('cart:updated', (event) => {
        const cart = event.detail.cart;
        if (!cart) return;
        $$('[data-cart-count]').forEach((el) => {
          el.textContent = cart.item_count;
          el.classList.toggle('is-empty', cart.item_count === 0);
        });
      });
    },
  };

  /* ==========================================================================
     Boot
     ========================================================================== */

  const boot = () => {
    Motion.init();
    Toast.init();
    Layout.init();
    Prefetch.init();
    Drawers.init();
    PriceSync.init();
    CartBadge.init();
    CartStore.fetchCart().then(() => CartStore.emit({ action: 'init' }));

    on(document, 'click', (event) => {
      const toggle = event.target.closest('[data-cart-toggle]');
      if (toggle && config.cartType === 'drawer') {
        event.preventDefault();
        document.dispatchEvent(new CustomEvent('cart-drawer:open'));
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-scan for reveal targets whenever a section is re-rendered.
  document.addEventListener('shopify:section:load', (event) => {
    Motion.scan(event.target);
    Layout.measure();
  });
  document.addEventListener('shopify:section:select', () => Layout.measure());
})();
